import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaApple, FaGoogle } from "react-icons/fa";
import { auth } from "../firebase";
import { useAuth } from "../auth/AuthContext";
import { signInWithApple, signInWithGoogle } from "../lib/socialAuth";
import Seo from "../components/Seo";
import { Mark, MetaLabel, CornerTick } from "../brand/Brand";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();

  const goAfterAuth = () => {
    const from = location.state?.from?.pathname;
    if (from) navigate(from, { replace: true });
    else if (profile?.isAdmin) navigate("/admin", { replace: true });
    else navigate("/dashboard", { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      goAfterAuth();
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSocial = (providerFn) => async () => {
    setError(null);
    setSubmitting(true);
    try {
      const cred = await providerFn();
      if (cred) goAfterAuth(); // null = user closed the popup
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fw-login">
      <Seo
        title="Sign in — bboy creative"
        description="Client portal sign-in for bboy creative."
        path="/login"
        noindex
      />
      <aside className="fw-login__panel">
        <Link to="/" className="fw-login__brand">
          <Mark size={40} />
        </Link>

        <div className="fw-login__headline">
          <MetaLabel size={11}>Studio · Client portal</MetaLabel>
          <h1 style={{ color: "var(--color-fg)" }}>
            Sign in to track your
            <br />
            project <span className="fw-login__accent">in motion</span>.
          </h1>
          <p>
            Milestones, approvals, invoices, and direct messages with your
            project team — all in one workspace.
          </p>
        </div>

        <div className="fw-login__meta">
          <MetaLabel size={10}>San Antonio · EST. 2024</MetaLabel>
        </div>
      </aside>

      <main className="fw-login__main">
        <div className="fw-login__card">
          <CornerTick pos="tl" />
          <CornerTick pos="tr" />
          <CornerTick pos="bl" />
          <CornerTick pos="br" />

          <div className="fw-login__card-head">
            <MetaLabel size={10}>Form · 01</MetaLabel>
            <h2>Sign in</h2>
            <p>Enter the credentials we sent when your project kicked off.</p>
          </div>

          <form onSubmit={onSubmit} className="fw-login__form">
            <div className="fw-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="fw-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="fw-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                className="fw-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && <div className="fw-login__error">{error}</div>}

            <button
              type="submit"
              className="fw-btn fw-btn--primary fw-login__submit"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in →"}
            </button>
          </form>

          <div className="fw-login__divider">
            <span>OR</span>
          </div>

          <div className="fw-login__sso">
            <button
              type="button"
              className="fw-login__sso-btn"
              onClick={onSocial(signInWithApple)}
              disabled={submitting}
            >
              <FaApple size={16} aria-hidden />
              Continue with Apple
            </button>
            <button
              type="button"
              className="fw-login__sso-btn"
              onClick={onSocial(signInWithGoogle)}
              disabled={submitting}
            >
              <FaGoogle size={15} aria-hidden />
              Continue with Google
            </button>
          </div>

          <div className="fw-login__foot">
            <MetaLabel size={10}>Need access?</MetaLabel>
            <a href="/#contact">Contact the studio</a>
          </div>
        </div>
      </main>
    </div>
  );
};

const humanizeError = (err) => {
  const code = err?.code || "";
  if (
    code.includes("invalid-credential") ||
    code.includes("wrong-password") ||
    code.includes("user-not-found")
  )
    return "Email or password doesn't match our records.";
  if (code.includes("too-many-requests"))
    return "Too many attempts. Try again in a moment.";
  if (code.includes("account-exists-with-different-credential"))
    return "An account already exists with this email — try the provider you originally signed up with.";
  if (code.includes("operation-not-allowed"))
    return "That sign-in method isn't enabled yet.";
  if (code.includes("network")) return "Network error. Check your connection.";
  return err?.message || "Sign in failed.";
};

export default Login;
